// src/pages/Welcome.jsx
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react"; // رجعنا مكتبة الأنيميشن
import {
  FaUserGraduate,
  FaArrowRight,
  FaTimes,
  FaRocket,
  FaInfoCircle,
} from "react-icons/fa";
import robotAnimation from "../assets/animations/robot.json"; // رجعنا ملف الروبوت
import { AuthContext } from "../context/AuthContext";
import FullPageLoader from "../components/ui/fullPageLoader";
import "bootstrap/dist/css/bootstrap.min.css";
import { useSound } from "../hooks/useSound";

export default function Welcome() {
  const { loading, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [greeting, setGreeting] = useState({ ar: "", en: "" });

  const clickSound = useSound("/sounds/click.mp3");

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) {
      setGreeting({ ar: "صباح الخير", en: "Good Morning" });
    } else if (hour < 18) {
      setGreeting({ ar: "مساء الخير", en: "Good Afternoon" });
    } else {
      setGreeting({ ar: "مساء الخير", en: "Good Evening" });
    }
  }, []);

  const handleStart = () => {
    localStorage.setItem("userType", "student");
    setShowAuthModal(true);
  };

  if (loading) return <FullPageLoader loadingName="جاري التحضير... | Initializing..." />;

  return (
    <div
      className="position-relative w-100 d-flex align-items-center justify-content-center overflow-hidden"
      style={{
        minHeight: "100vh",
        background: "#09090b",
        fontFamily: "'Inter', system-ui, sans-serif",
        marginTop: "50px",
      }}
    >
      {/* --- Background Ambient Effects --- */}
      <div className="position-absolute w-100 h-100 pe-none">
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 15, repeat: Infinity }}
          className="position-absolute rounded-circle"
          style={{
            width: "120vw",
            height: "120vw",
            background:
              "radial-gradient(circle, rgba(14, 165, 233, 0.07) 0%, transparent 60%)",
            top: "-50%",
            insetInlineEnd: "-20%",
            filter: "blur(80px)",
          }}
        />
        <div
          className="position-absolute w-100 h-100"
          style={{
            backgroundImage:
              "url('https://grainy-gradients.vercel.app/noise.svg')",
            opacity: 0.03,
          }}
        ></div>
      </div>

      <div className="container position-relative z-2 py-5">
        <div className="row justify-content-center align-items-center gy-5">
          
          {/* =======================
              1. Text Section 
              ======================= */}
          <div className="col-12 col-lg-6 text-center text-lg-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="d-flex flex-column align-items-center align-items-lg-start"
            >
              {/* Badge */}
              <div
                className="d-inline-flex align-items-center gap-2 mb-4 px-3 py-1 rounded-pill"
                style={{
                  background: "rgba(56, 189, 248, 0.1)",
                  border: "1px solid rgba(56, 189, 248, 0.2)",
                }}
              >
                <span
                  className="rounded-pill p-2 bg-primary fw-bold"
                  style={{ fontSize: "0.7rem", letterSpacing: "1px" }}
                >
                  BETA
                </span>
                <span className="text-info fw-bold" style={{ fontSize: "0.85rem" }}>
                  CampusPulse v2.0
                </span>
              </div>

              {/* Hero Title - ثنائي اللغة */}
              <div className="mb-4 d-flex flex-column gap-2 w-100">
                <h1 
                  dir="rtl" 
                  className="fw-bolder text-white m-0 text-center text-lg-end" 
                  style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", lineHeight: "1.2" }}
                >
                  مستقبل <br className="d-none d-md-block" />
                  <span
                    className="text-transparent"
                    style={{
                      background: "linear-gradient(to right, #38bdf8, #818cf8)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    الحياة الجامعية
                  </span>
                </h1>
                
                <h2 
                  dir="ltr" 
                  className="fw-bolder m-0 text-center text-lg-start" 
                  style={{ fontSize: "clamp(1.5rem, 3vw, 2.5rem)", lineHeight: "1.1", opacity: 0.7, color: "#fff" }}
                >
                  Future of <br className="d-none d-md-block" />
                  <span
                    className="text-transparent"
                    style={{
                      background: "linear-gradient(to right, #38bdf8, #818cf8)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Campus Life
                  </span>
                </h2>
              </div>

              {/* Description - ثنائي اللغة */}
              <div className="mb-5 d-flex flex-column gap-3 w-100" style={{ maxWidth: "550px" }}>
                <p 
                  dir="rtl" 
                  className="text-light m-0 text-center text-lg-end" 
                  style={{ fontSize: "clamp(1.1rem, 1.5vw, 1.25rem)", lineHeight: "1.6" }}
                >
                  أدر فعالياتك، تتبع حضورك، وتواصل مع مجتمعك الجامعي من خلال منصة ذكية واحدة.
                </p>
                <p 
                  dir="ltr" 
                  className="text-secondary m-0 text-center text-lg-start" 
                  style={{ fontSize: "clamp(0.95rem, 1.2vw, 1.1rem)", lineHeight: "1.5" }}
                >
                  Manage events, track attendance, and connect with your university community in one smart platform.
                </p>
              </div>

              {/* Action Buttons */}
              {!user ? (
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-start w-100">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleStart}
                    className="btn btn-primary px-4 py-3 rounded-pill shadow-lg border-0 d-flex flex-column align-items-center justify-content-center gap-1"
                    style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)", minWidth: "180px" }}
                  >
                    <span className="fw-bold d-flex align-items-center gap-2" style={{ fontSize: "1.1rem" }}>
                      ابدأ الآن <FaRocket />
                    </span>
                    <span style={{ fontSize: "0.8rem", opacity: 0.8, fontWeight: "600" }}>Get Started</span>
                  </motion.button>

                  <button
                    onClick={() => navigate("/about")}
                    className="btn btn-outline-light px-4 py-3 rounded-pill d-flex flex-column align-items-center justify-content-center gap-1"
                    style={{ borderColor: "rgba(255,255,255,0.15)", background: "transparent", minWidth: "180px" }}
                  >
                    <span className="fw-bold d-flex align-items-center gap-2" style={{ fontSize: "1.1rem" }}>
                      اعرف المزيد <FaInfoCircle />
                    </span>
                    <span style={{ fontSize: "0.8rem", opacity: 0.7, fontWeight: "600" }}>Learn More</span>
                  </button>
                </div>
              ) : (
                <div
                  className="p-4 rounded-4 text-start border border-primary border-opacity-25 w-100"
                  style={{ background: "rgba(14, 165, 233, 0.05)", maxWidth: "450px" }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="text-white fw-bold m-0" dir="ltr">{greeting.en}, {user.fullName.split(" ")[0]}!</h5>
                    <h5 className="text-white fw-bold m-0" dir="rtl">{greeting.ar}</h5>
                  </div>
                  
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <p className="text-white-50 small m-0" dir="ltr">Ready to explore?</p>
                    <p className="text-white-50 small m-0" dir="rtl">مستعد للاستكشاف؟</p>
                  </div>

                  <button
                    onClick={() => {
                      navigate("/home");
                      clickSound();
                    }}
                    className="btn btn-primary w-100 rounded-pill py-2 d-flex flex-column align-items-center justify-content-center gap-1"
                  >
                    <span className="fw-bold d-flex align-items-center gap-2" style={{ fontSize: "1.1rem" }}>
                      لوحة التحكم <FaArrowRight />
                    </span>
                    <span style={{ fontSize: "0.85rem", opacity: 0.8 }}>Dashboard</span>
                  </button>
                </div>
              )}
            </motion.div>
          </div>

          {/* =======================
              2. Robot Section (He is Back!) 
              ======================= */}
          <div className="col-12 col-lg-6 d-flex justify-content-center align-items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="position-relative w-100 d-flex justify-content-center"
            >
              {/* هالة الإضاءة ورا الروبوت */}
              <div
                className="position-absolute top-50 start-50 translate-middle rounded-circle bg-primary"
                style={{
                  width: "300px",
                  height: "300px",
                  opacity: 0.15,
                  filter: "blur(60px)",
                }}
              ></div>

              {/* الروبوت نفسه */}
              <div style={{ maxWidth: "450px", width: "100%", zIndex: 2 }}>
                <Lottie animationData={robotAnimation} loop />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* =======================
          3. Auth Modal (Bilingual)
          ======================= */}
      <AnimatePresence>
        {showAuthModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-end align-items-md-center justify-content-center p-0 p-md-3"
            style={{
              zIndex: 1050,
              background: "rgba(0,0,0,0.8)",
              backdropFilter: "blur(12px)",
            }}
            onClick={() => setShowAuthModal(false)}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-dark w-100 p-5 rounded-top-5 rounded-md-5 border border-white border-opacity-10 position-relative"
              style={{ maxWidth: "450px", background: "#18181b" }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowAuthModal(false)}
                className="position-absolute top-0 end-0 m-4 btn btn-dark rounded-circle p-2 shadow-sm border border-secondary border-opacity-25"
              >
                <FaTimes color="white" />
              </button>

              <div className="text-center mb-4 mt-2">
                <div
                  className="mx-auto mb-3 rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center border border-primary border-opacity-25"
                  style={{ width: "75px", height: "75px", boxShadow: "0 0 20px rgba(14, 165, 233, 0.2)" }}
                >
                  <FaUserGraduate size={32} className="text-primary" />
                </div>
                <div className="d-flex flex-column gap-1 mb-2">
                  <h3 className="fw-bold text-white mb-0" dir="rtl">بوابة الطلاب</h3>
                  <h4 className="text-white-50 mb-0" dir="ltr" style={{ fontSize: "1.1rem" }}>Student Access</h4>
                </div>
                <p className="text-secondary small mt-3 px-3 d-flex flex-column gap-1">
                  <span dir="rtl">سجل دخولك للوصول إلى لوحة التحكم الخاصة بك</span>
                  <span dir="ltr">Login to access your university dashboard</span>
                </p>
              </div>

              <div className="d-flex flex-column gap-3">
                <button
                  onClick={() => navigate("/auth/login")}
                  className="btn btn-primary py-3 rounded-4 d-flex flex-column align-items-center justify-content-center gap-1 border-0"
                  style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)" }}
                >
                  <span className="fw-bold text-white" style={{ fontSize: "1.1rem" }}>تسجيل الدخول</span>
                  <span className="text-white-50" style={{ fontSize: "0.85rem", fontWeight: "600" }}>Log In</span>
                </button>
                <button
                  onClick={() => navigate("/auth/register")}
                  className="btn btn-outline-secondary py-3 rounded-4 d-flex flex-column align-items-center justify-content-center gap-1 border-opacity-50"
                  style={{ background: "rgba(255,255,255,0.03)" }}
                >
                  <span className="fw-bold text-white" style={{ fontSize: "1.1rem" }}>إنشاء حساب</span>
                  <span className="text-secondary" style={{ fontSize: "0.85rem", fontWeight: "600" }}>Create Account</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// // src/pages/Welcome.jsx
// import React, { useState, useEffect, useContext } from "react";
// import { useNavigate } from "react-router-dom";
// import { motion, AnimatePresence } from "framer-motion";
// import {
//   FaUserGraduate,
//   FaArrowRight,
//   FaTimes,
//   FaRocket,
//   FaInfoCircle,
//   FaCalendarCheck,
//   FaShieldAlt
// } from "react-icons/fa";
// import { AuthContext } from "../context/AuthContext";
// import FullPageLoader from "../components/ui/fullPageLoader";
// import "bootstrap/dist/css/bootstrap.min.css";
// import { useSound } from "../hooks/useSound";

// export default function Welcome() {
//   const { loading, user } = useContext(AuthContext);
//   const navigate = useNavigate();
//   const [showAuthModal, setShowAuthModal] = useState(false);
//   const [greeting, setGreeting] = useState({ ar: "", en: "" });

//   const clickSound = useSound("/sounds/click.mp3");

//   useEffect(() => {
//     const hour = new Date().getHours();
//     if (hour < 12) {
//       setGreeting({ ar: "صباح الخير", en: "Good Morning" });
//     } else if (hour < 18) {
//       setGreeting({ ar: "مساء الخير", en: "Good Afternoon" });
//     } else {
//       setGreeting({ ar: "مساء الخير", en: "Good Evening" });
//     }
//   }, []);

//   const handleStart = () => {
//     localStorage.setItem("userType", "student");
//     setShowAuthModal(true);
//   };

//   if (loading) return <FullPageLoader loadingName="جاري التحضير... | Initializing..." />;

//   return (
//     <div
//       className="position-relative w-100 d-flex align-items-center justify-content-center overflow-hidden"
//       style={{
//         minHeight: "100vh",
//         background: "#09090b",
//         fontFamily: "'Inter', system-ui, sans-serif",
//         marginTop: "50px",
//       }}
//     >
//       {/* --- Background Ambient Effects --- */}
//       <div className="position-absolute w-100 h-100 pe-none">
//         <motion.div
//           animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
//           transition={{ duration: 15, repeat: Infinity }}
//           className="position-absolute rounded-circle"
//           style={{
//             width: "120vw",
//             height: "120vw",
//             background:
//               "radial-gradient(circle, rgba(14, 165, 233, 0.07) 0%, transparent 60%)",
//             top: "-50%",
//             insetInlineEnd: "-20%", // Logical property
//             filter: "blur(80px)",
//           }}
//         />
//         <div
//           className="position-absolute w-100 h-100"
//           style={{
//             backgroundImage:
//               "url('https://grainy-gradients.vercel.app/noise.svg')",
//             opacity: 0.03,
//           }}
//         ></div>
//       </div>

//       <div className="container position-relative z-2 py-5">
//         <div className="row justify-content-center align-items-center gy-5">
          
//           {/* =======================
//               1. Text Section 
//               ======================= */}
//           <div className="col-12 col-lg-6 text-center text-lg-start">
//             <motion.div
//               initial={{ opacity: 0, y: 30 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.8, ease: "easeOut" }}
//               className="d-flex flex-column align-items-center align-items-lg-start"
//             >
//               {/* Badge */}
//               <div
//                 className="d-inline-flex align-items-center gap-2 mb-4 px-3 py-1 rounded-pill"
//                 style={{
//                   background: "rgba(56, 189, 248, 0.1)",
//                   border: "1px solid rgba(56, 189, 248, 0.2)",
//                 }}
//               >
//                 <span
//                   className="rounded-pill p-2 bg-primary fw-bold"
//                   style={{ fontSize: "0.7rem", letterSpacing: "1px" }}
//                 >
//                   BETA
//                 </span>
//                 <span className="text-info fw-bold" style={{ fontSize: "0.85rem" }}>
//                   CampusPulse v2.0
//                 </span>
//               </div>

//               {/* Hero Title - ثنائي اللغة */}
//               <div className="mb-4 d-flex flex-column gap-2 w-100">
//                 <h1 
//                   dir="rtl" 
//                   className="fw-bolder text-white m-0 text-center text-lg-end" 
//                   style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", lineHeight: "1.2" }}
//                 >
//                   مستقبل <br className="d-none d-md-block" />
//                   <span
//                     className="text-transparent"
//                     style={{
//                       background: "linear-gradient(to right, #38bdf8, #818cf8)",
//                       WebkitBackgroundClip: "text",
//                       WebkitTextFillColor: "transparent",
//                     }}
//                   >
//                     الحياة الجامعية
//                   </span>
//                 </h1>
                
//                 <h2 
//                   dir="ltr" 
//                   className="fw-bolder m-0 text-center text-lg-start" 
//                   style={{ fontSize: "clamp(1.5rem, 3vw, 2.5rem)", lineHeight: "1.1", opacity: 0.7, color: "#fff" }}
//                 >
//                   Future of <br className="d-none d-md-block" />
//                   <span
//                     className="text-transparent"
//                     style={{
//                       background: "linear-gradient(to right, #38bdf8, #818cf8)",
//                       WebkitBackgroundClip: "text",
//                       WebkitTextFillColor: "transparent",
//                     }}
//                   >
//                     Campus Life
//                   </span>
//                 </h2>
//               </div>

//               {/* Description - ثنائي اللغة */}
//               <div className="mb-5 d-flex flex-column gap-3 w-100" style={{ maxWidth: "550px" }}>
//                 <p 
//                   dir="rtl" 
//                   className="text-light m-0 text-center text-lg-end" 
//                   style={{ fontSize: "clamp(1.1rem, 1.5vw, 1.25rem)", lineHeight: "1.6" }}
//                 >
//                   أدر فعالياتك، تتبع حضورك، وتواصل مع مجتمعك الجامعي من خلال منصة ذكية واحدة.
//                 </p>
//                 <p 
//                   dir="ltr" 
//                   className="text-secondary m-0 text-center text-lg-start" 
//                   style={{ fontSize: "clamp(0.95rem, 1.2vw, 1.1rem)", lineHeight: "1.5" }}
//                 >
//                   Manage events, track attendance, and connect with your university community in one smart platform.
//                 </p>
//               </div>

//               {/* Action Buttons */}
//               {!user ? (
//                 <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-start w-100">
//                   <motion.button
//                     whileHover={{ scale: 1.05 }}
//                     whileTap={{ scale: 0.95 }}
//                     onClick={handleStart}
//                     className="btn btn-primary px-4 py-3 rounded-pill shadow-lg border-0 d-flex flex-column align-items-center justify-content-center gap-1"
//                     style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)", minWidth: "180px" }}
//                   >
//                     <span className="fw-bold d-flex align-items-center gap-2" style={{ fontSize: "1.1rem" }}>
//                       ابدأ الآن <FaRocket />
//                     </span>
//                     <span style={{ fontSize: "0.8rem", opacity: 0.8, fontWeight: "600" }}>Get Started</span>
//                   </motion.button>

//                   <button
//                     onClick={() => navigate("/about")}
//                     className="btn btn-outline-light px-4 py-3 rounded-pill d-flex flex-column align-items-center justify-content-center gap-1"
//                     style={{ borderColor: "rgba(255,255,255,0.15)", background: "transparent", minWidth: "180px" }}
//                   >
//                     <span className="fw-bold d-flex align-items-center gap-2" style={{ fontSize: "1.1rem" }}>
//                       اعرف المزيد <FaInfoCircle />
//                     </span>
//                     <span style={{ fontSize: "0.8rem", opacity: 0.7, fontWeight: "600" }}>Learn More</span>
//                   </button>
//                 </div>
//               ) : (
//                 <div
//                   className="p-4 rounded-4 text-start border border-primary border-opacity-25 w-100"
//                   style={{ background: "rgba(14, 165, 233, 0.05)", maxWidth: "450px" }}
//                 >
//                   <div className="d-flex justify-content-between align-items-center mb-2">
//                     <h5 className="text-white fw-bold m-0" dir="ltr">{greeting.en}, {user.fullName.split(" ")[0]}!</h5>
//                     <h5 className="text-white fw-bold m-0" dir="rtl">{greeting.ar}</h5>
//                   </div>
                  
//                   <div className="d-flex justify-content-between align-items-center mb-4">
//                     <p className="text-white-50 small m-0" dir="ltr">Ready to explore?</p>
//                     <p className="text-white-50 small m-0" dir="rtl">مستعد للاستكشاف؟</p>
//                   </div>

//                   <button
//                     onClick={() => {
//                       navigate("/home");
//                       clickSound();
//                     }}
//                     className="btn btn-primary w-100 rounded-pill py-2 d-flex flex-column align-items-center justify-content-center gap-1"
//                   >
//                     <span className="fw-bold d-flex align-items-center gap-2" style={{ fontSize: "1.1rem" }}>
//                       لوحة التحكم <FaArrowRight />
//                     </span>
//                     <span style={{ fontSize: "0.85rem", opacity: 0.8 }}>Dashboard</span>
//                   </button>
//                 </div>
//               )}
//             </motion.div>
//           </div>

//           {/* =======================
//               2. Glassmorphism Visual 
//               ======================= */}
//           <div className="col-12 col-lg-6 d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
//             <motion.div
//               initial={{ opacity: 0, scale: 0.8 }}
//               animate={{ opacity: 1, scale: 1 }}
//               transition={{ duration: 1, delay: 0.2 }}
//               className="position-relative w-100 d-flex justify-content-center"
//             >
//               {/* Central Glow */}
//               <div
//                 className="position-absolute top-50 start-50 translate-middle rounded-circle bg-primary"
//                 style={{ width: "280px", height: "280px", opacity: 0.2, filter: "blur(70px)" }}
//               ></div>

//               {/* Main Floating Glass Card */}
//               <motion.div
//                 animate={{ y: [-10, 10, -10] }}
//                 transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
//                 className="position-relative z-2 p-4 rounded-4 border border-white border-opacity-10 shadow-lg"
//                 style={{ background: "rgba(255, 255, 255, 0.03)", backdropFilter: "blur(16px)", width: "280px" }}
//               >
//                 <div className="d-flex align-items-center gap-3 mb-4">
//                   <div className="rounded-circle bg-primary bg-opacity-20 p-3">
//                     <FaUserGraduate size={24} className="text-primary" />
//                   </div>
//                   <div className="w-100">
//                     <div className="bg-white bg-opacity-20 rounded-pill mb-2" style={{ width: "60%", height: "8px" }}></div>
//                     <div className="bg-white bg-opacity-10 rounded-pill" style={{ width: "80%", height: "8px" }}></div>
//                   </div>
//                 </div>
//                 {/* Mockup Rows */}
//                 <div className="bg-white bg-opacity-10 rounded-3 w-100 mb-3 d-flex align-items-center p-3 gap-3" style={{ height: "60px" }}>
//                    <FaCalendarCheck className="text-info opacity-75" size={20} />
//                    <div className="bg-white bg-opacity-20 rounded-pill w-50" style={{ height: "6px" }}></div>
//                 </div>
//                 <div className="bg-white bg-opacity-10 rounded-3 w-100 d-flex align-items-center p-3 gap-3" style={{ height: "60px" }}>
//                    <FaShieldAlt className="text-success opacity-75" size={20} />
//                    <div className="bg-white bg-opacity-20 rounded-pill w-75" style={{ height: "6px" }}></div>
//                 </div>
//               </motion.div>

//               {/* Secondary Floating Card */}
//               <motion.div
//                 animate={{ y: [10, -10, 10] }}
//                 transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
//                 className="position-absolute z-3 p-3 rounded-4 border border-info border-opacity-25 shadow-lg d-none d-sm-block"
//                 style={{ background: "rgba(14, 165, 233, 0.08)", backdropFilter: "blur(12px)", width: "160px", bottom: "-5%", right: "10%" }}
//               >
//                 <div className="d-flex align-items-center gap-2 mb-2">
//                   <FaRocket className="text-info" />
//                   <div className="bg-white bg-opacity-20 rounded-pill w-50" style={{ height: "6px" }}></div>
//                 </div>
//                 <div className="bg-white bg-opacity-10 rounded-2 w-100 mt-3" style={{ height: "20px" }}></div>
//               </motion.div>
//             </motion.div>
//           </div>
//         </div>
//       </div>

//       {/* =======================
//           3. Auth Modal (Bilingual)
//           ======================= */}
//       <AnimatePresence>
//         {showAuthModal && (
//           <motion.div
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//             className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-end align-items-md-center justify-content-center p-0 p-md-3"
//             style={{
//               zIndex: 1050,
//               background: "rgba(0,0,0,0.8)",
//               backdropFilter: "blur(12px)",
//             }}
//             onClick={() => setShowAuthModal(false)}
//           >
//             <motion.div
//               initial={{ y: "100%" }}
//               animate={{ y: 0 }}
//               exit={{ y: "100%" }}
//               transition={{ type: "spring", damping: 25, stiffness: 300 }}
//               className="bg-dark w-100 p-5 rounded-top-5 rounded-md-5 border border-white border-opacity-10 position-relative"
//               style={{ maxWidth: "450px", background: "#18181b" }}
//               onClick={(e) => e.stopPropagation()}
//             >
//               <button
//                 onClick={() => setShowAuthModal(false)}
//                 className="position-absolute top-0 end-0 m-4 btn btn-dark rounded-circle p-2 shadow-sm border border-secondary border-opacity-25"
//               >
//                 <FaTimes color="white" />
//               </button>

//               <div className="text-center mb-4 mt-2">
//                 <div
//                   className="mx-auto mb-3 rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center border border-primary border-opacity-25"
//                   style={{ width: "75px", height: "75px", boxShadow: "0 0 20px rgba(14, 165, 233, 0.2)" }}
//                 >
//                   <FaUserGraduate size={32} className="text-primary" />
//                 </div>
//                 <div className="d-flex flex-column gap-1 mb-2">
//                   <h3 className="fw-bold text-white mb-0" dir="rtl">بوابة الطلاب</h3>
//                   <h4 className="text-white-50 mb-0" dir="ltr" style={{ fontSize: "1.1rem" }}>Student Access</h4>
//                 </div>
//                 <p className="text-secondary small mt-3 px-3 d-flex flex-column gap-1">
//                   <span dir="rtl">سجل دخولك للوصول إلى لوحة التحكم الخاصة بك</span>
//                   <span dir="ltr">Login to access your university dashboard</span>
//                 </p>
//               </div>

//               <div className="d-flex flex-column gap-3">
//                 <button
//                   onClick={() => navigate("/auth/login")}
//                   className="btn btn-primary py-3 rounded-4 d-flex flex-column align-items-center justify-content-center gap-1 border-0"
//                   style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)" }}
//                 >
//                   <span className="fw-bold text-white" style={{ fontSize: "1.1rem" }}>تسجيل الدخول</span>
//                   <span className="text-white-50" style={{ fontSize: "0.85rem", fontWeight: "600" }}>Log In</span>
//                 </button>
//                 <button
//                   onClick={() => navigate("/auth/register")}
//                   className="btn btn-outline-secondary py-3 rounded-4 d-flex flex-column align-items-center justify-content-center gap-1 border-opacity-50"
//                   style={{ background: "rgba(255,255,255,0.03)" }}
//                 >
//                   <span className="fw-bold text-white" style={{ fontSize: "1.1rem" }}>إنشاء حساب</span>
//                   <span className="text-secondary" style={{ fontSize: "0.85rem", fontWeight: "600" }}>Create Account</span>
//                 </button>
//               </div>
//             </motion.div>
//           </motion.div>
//         )}
//       </AnimatePresence>
//     </div>
//   );
// }

/* **********************************----------------------------------------------********** */
/* **********************************----------------------------------------------********** */
/* **********************************----------------------------------------------********** */
/* **********************************----------------------------------------------********** */
/* **********************************----------------------------------------------********** */
/* **********************************----------------------------------------------********** */
/* **********************************----------------------------------------------********** */
/* **********************************----------------------------------------------********** */

// // src/pages/Welcome.jsx
// import React, { useState, useEffect, useContext } from "react";
// import { useNavigate } from "react-router-dom";
// import { motion, AnimatePresence } from "framer-motion";
// import Lottie from "lottie-react";
// import {
//   FaUserGraduate,
//   FaArrowRight,
//   FaTimes,
//   FaRocket,
//   FaInfoCircle,
// } from "react-icons/fa";
// import robotAnimation from "../assets/animations/robot.json";
// import { AuthContext } from "../context/AuthContext";
// import FullPageLoader from "../components/ui/fullPageLoader";
// import "bootstrap/dist/css/bootstrap.min.css";
// import { useSound } from "../hooks/useSound";

// export default function Welcome() {
//   const { loading, user } = useContext(AuthContext);
//   const navigate = useNavigate();
//   const [showAuthModal, setShowAuthModal] = useState(false);
//   const [, setGreeting] = useState("");

//   const clickSound = useSound("/sounds/click.mp3");

//   useEffect(() => {
//     const hour = new Date().getHours();
//     setGreeting(
//       hour < 12
//         ? "Good Morning"
//         : hour < 18
//           ? "Good Afternoon"
//           : "Good Evening",
//     );
//   }, []);

//   const handleStart = () => {
//     localStorage.setItem("userType", "student");
//     setShowAuthModal(true);
//   };

//   if (loading) return <FullPageLoader loadingName="Initializing..." />;

//   return (
//     <div
//       className="position-relative w-100 d-flex align-items-center justify-content-center overflow-hidden"
//       style={{
//         minHeight: "100vh",
//         background: "#09090b",
//         fontFamily: "'Inter', system-ui, sans-serif",
//         marginTop: "50px",
//       }}
//     >
//       {/* --- Background Ambient Effects --- */}
//       <div className="position-absolute w-100 h-100 pe-none">
//         <motion.div
//           animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
//           transition={{ duration: 15, repeat: Infinity }}
//           className="position-absolute rounded-circle"
//           style={{
//             width: "120vw",
//             height: "120vw",
//             background:
//               "radial-gradient(circle, rgba(14, 165, 233, 0.07) 0%, transparent 60%)",
//             top: "-50%",
//             right: "-20%",
//             filter: "blur(80px)",
//           }}
//         />
//         <div
//           className="position-absolute w-100 h-100"
//           style={{
//             backgroundImage:
//               "url('https://grainy-gradients.vercel.app/noise.svg')",
//             opacity: 0.03,
//           }}
//         ></div>
//       </div>

//       <div className="container position-relative z-2 py-5">
//         <div className="row justify-content-center align-items-center gy-5">
//           {/* Text Section */}
//           <div className="col-12 col-lg-6 text-center text-lg-start">
//             <motion.div
//               initial={{ opacity: 0, y: 30 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.8, ease: "easeOut" }}
//             >
//               {/* Badge */}
//               <div
//                 className="d-inline-flex align-items-center gap-2 mb-4 px-3 py-1 rounded-pill"
//                 style={{
//                   background: "rgba(56, 189, 248, 0.1)",
//                   border: "1px solid rgba(56, 189, 248, 0.2)",
//                 }}
//               >
//                 <span
//                   className="rounded-pill p-2 bg-primary"
//                   style={{ fontSize: "0.7rem" }}
//                 >
//                   BETA
//                 </span>
//                 <span
//                   className="text-info small fw-bold"
//                   style={{ fontSize: "0.8rem" }}
//                 >
//                   CampusPulse v2.0
//                 </span>
//               </div>

//               {/* Hero Title - Responsive Font Size */}
//               <h1
//                 className="fw-bolder text-white mb-3 tracking-tight"
//                 style={{
//                   fontSize: "clamp(2.5rem, 5vw, 4.5rem)",
//                   lineHeight: "1.1",
//                 }}
//               >
//                 Future of <br />
//                 <span
//                   className="text-transparent"
//                   style={{
//                     background: "linear-gradient(to right, #38bdf8, #818cf8)",
//                     WebkitBackgroundClip: "text",
//                     WebkitTextFillColor: "transparent",
//                   }}
//                 >
//                   Campus Life
//                 </span>
//               </h1>

//               <p
//                 className="lead text-secondary mb-5 pe-lg-5 mx-auto mx-lg-0"
//                 style={{
//                   fontSize: "clamp(1rem, 1.5vw, 1.25rem)",
//                   maxWidth: "500px",
//                 }}
//               >
//                 Manage events, track attendance, and connect with your
//                 university community in one smart platform.
//               </p>

//               {/* Action Buttons */}
//               {!user ? (
//                 <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-start">
//                   <motion.button
//                     whileHover={{ scale: 1.05 }}
//                     whileTap={{ scale: 0.95 }}
//                     onClick={handleStart}
//                     className="btn btn-primary btn-lg px-5 py-3 rounded-pill fw-bold shadow-lg border-0 d-flex align-items-center justify-content-center gap-2"
//                     style={{
//                       background:
//                         "linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)",
//                     }}
//                   >
//                     <FaRocket /> Get Started
//                   </motion.button>

//                   <button
//                     onClick={() => navigate("/about")} // ربطنا صفحة الـ About هنا
//                     className="btn btn-outline-light btn-lg px-4 py-3 rounded-pill fw-medium d-flex align-items-center justify-content-center gap-2"
//                     style={{
//                       borderColor: "rgba(255,255,255,0.15)",
//                       background: "transparent",
//                     }}
//                   >
//                     <FaInfoCircle /> Learn More
//                   </button>
//                 </div>
//               ) : (
//                 <div
//                   className="p-4 rounded-4 text-start d-inline-block border border-primary border-opacity-25"
//                   style={{ background: "rgba(14, 165, 233, 0.05)" }}
//                 >
//                   <h5 className="text-white fw-bold mb-1">
//                     Welcome back, {user.fullName.split(" ")[0]}!
//                   </h5>
//                   <p className="text-white-50 small mb-3">
//                     Ready to explore new activities?
//                   </p>
//                   <button
//                     onClick={() => {
//                       navigate("/home");
//                       clickSound();
//                     }}
//                     className="btn btn-primary w-100 rounded-pill fw-bold"
//                   >
//                     Go to Dashboard <FaArrowRight className="ms-2" />
//                   </button>
//                 </div>
//               )}
//             </motion.div>
//           </div>

//           {/* Robot Section */}
//           <div className="col-12 col-lg-6 d-flex justify-content-center">
//             <motion.div
//               initial={{ opacity: 0, scale: 0.8 }}
//               animate={{ opacity: 1, scale: 1 }}
//               transition={{ duration: 1, delay: 0.2 }}
//               className="position-relative w-100 d-flex justify-content-center"
//             >
//               <div
//                 className="position-absolute top-50 start-50 translate-middle rounded-circle bg-primary"
//                 style={{
//                   width: "300px",
//                   height: "300px",
//                   opacity: 0.15,
//                   filter: "blur(60px)",
//                 }}
//               ></div>

//               <div style={{ maxWidth: "450px", width: "100%" }}>
//                 <Lottie animationData={robotAnimation} loop />
//               </div>
//             </motion.div>
//           </div>
//         </div>
//       </div>

//       {/* Auth Modal (Cleaned up) */}
//       <AnimatePresence>
//         {showAuthModal && (
//           <motion.div
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//             className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-end align-items-md-center justify-content-center p-0 p-md-3"
//             style={{
//               zIndex: 1050,
//               background: "rgba(0,0,0,0.8)",
//               backdropFilter: "blur(12px)",
//             }}
//             onClick={() => setShowAuthModal(false)}
//           >
//             <motion.div
//               initial={{ y: "100%" }}
//               animate={{ y: 0 }}
//               exit={{ y: "100%" }}
//               transition={{ type: "spring", damping: 25, stiffness: 300 }}
//               className="bg-dark w-100 p-5 rounded-top-5 rounded-md-5 border border-white border-opacity-10 position-relative"
//               style={{ maxWidth: "450px", background: "#18181b" }}
//               onClick={(e) => e.stopPropagation()}
//             >
//               <button
//                 onClick={() => setShowAuthModal(false)}
//                 className="position-absolute top-0 end-0 m-4 btn btn-dark rounded-circle p-2 shadow-sm"
//               >
//                 <FaTimes color="white" />
//               </button>

//               <div className="text-center mb-4">
//                 <div
//                   className="mx-auto mb-3 rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center"
//                   style={{ width: "70px", height: "70px" }}
//                 >
//                   <FaUserGraduate size={30} className="text-primary" />
//                 </div>
//                 <h3 className="fw-bold text-white">Student Access</h3>
//                 <p className="text-secondary small">
//                   Login to access your university dashboard
//                 </p>
//               </div>

//               <div className="d-grid gap-3">
//                 <button
//                   onClick={() => navigate("/auth/login")}
//                   className="btn btn-primary py-3 rounded-3 fw-bold"
//                 >
//                   Log In
//                 </button>
//                 <button
//                   onClick={() => navigate("/auth/register")}
//                   className="btn btn-outline-secondary py-3 rounded-3 fw-bold text-white"
//                 >
//                   Create Account
//                 </button>
//               </div>
//             </motion.div>
//           </motion.div>
//         )}
//       </AnimatePresence>
//     </div>
//   );
// }
